import { gqlQuery } from "@/common/lib";
import { onlyUniqueByKey } from "@/common/utils";
import ReactTextareaAutocomplete from "@webscopeio/react-textarea-autocomplete";
import cn from "classnames";
import { CommentAnyUsersQuery } from "gql/users.graphql";
import React, { useState } from "react";
import SendIcon from "./SendIcon";
import { SanitizedTextareaAutosize } from "@/utils/xmlSanitizedTextAreaAutosize";

const Item = ({ entity: { name } }) => <div>{`${name}`}</div>;

const TextareaAutosizeWrapper = (props) => (
  <SanitizedTextareaAutosize {...props}
                    ref={props.innerRef}
                    placeholder="Leave a comment. Tag someone using @name." />
);

const Loading = ({ data }) => <div>Loading</div>;

interface IProps {
  className?: string
  value: string
  buttonSize?: number
  buttonClassName?: string
  onSave: (value: string) => void
  onCancel?: () => void
  autoFocus?: boolean
}

export const CommentInput = ({
  className,
  value = "",
  buttonSize = 20,
  buttonClassName = "py-2",
  onSave,
  onCancel = () => {},
  autoFocus
}: IProps) => {
  const [content, setContent] = useState(value);

  const disabled = !content || value === content;

  const handleSave = () => {
    if (disabled) {
      return;
    }

    setContent("");
    onSave(content);
  };

  const handleKeydown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave();
    } else if (e.key === "Escape") {
      onCancel();
    }
  };

  return gqlQuery(CommentAnyUsersQuery, (res) => (
    <div className={cn(className, "comment-input", "relative flex items-end")} data-testid="comment-input">
      <ReactTextareaAutocomplete
        textAreaComponent={{ component: TextareaAutosizeWrapper, ref: "innerRef" }}
        className="comment-input-box"
        loadingComponent={Loading}
        autoFocus={autoFocus}
        value={content}
        onKeyDown={handleKeydown}
        onChange={(e: any) => {
          e.target && setContent(e.target.value);
        }}
        trigger={{
          "@": {
            dataProvider: (token: string) => {
              const regexp = new RegExp(token, "i");
              return res.corsis_users
                .filter((user) => user.full_name.match(regexp))
                .map((user) => ({ name: user.full_name, token }))
                .filter(onlyUniqueByKey("name"));
            },
            component: Item,
            output: (item: any, trigger: string) => trigger + item.name
          }
        }}
      />
      <div
        className={cn(buttonClassName, "flex ml-16 cursor-pointer", { "opacity-20 is-disabled": disabled })}
        onClick={handleSave}
        data-testid="button-send-comment"
      >
        <SendIcon width={buttonSize} height={buttonSize} />
      </div>
    </div>
  ));
};
